import {
    ICampaignMessage,
    ICampaignUpdate,
    IDonationItem,
    IDonationSummary,
    IGrantItem,
    MyCampaignResponse,
} from '../types/campaign';
import { apiUrl, authHeaders } from './auth';
import queryString from 'query-string';

export const fetchMyCampaign = async (
    campaign_id?: string
): Promise<MyCampaignResponse> => {
    const headers = authHeaders();
    const baseUrl = apiUrl + '/v2/myCampaign';
    const url = campaign_id ? baseUrl + '?campaign_id=' + campaign_id : baseUrl;
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const putMyCampaign = async (payload): Promise<MyCampaignResponse> => {
    const headers = authHeaders();
    const url = apiUrl + '/v2/myCampaign';

    // Base64 encode the story to avoid upsetting OWASP firewall rules
    // in the reverse proxy
    const { story } = payload;
    if (story) {
        const utf8Bytes = new TextEncoder().encode(story);
        let binaryString = String.fromCharCode.apply(null, utf8Bytes);
        payload.base64_encoded_story = btoa(binaryString);
        payload.story = '';
    }

    const resp = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
    });
    const json = await resp.json();
    return json;
};

export const syncMyCampaign = async () => {
    const headers = authHeaders();
    const url = apiUrl + '/v2/syncMyCampaign';
    const resp = await fetch(url, {
        method: 'POST',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const publishCampaign = async (campaignId?: number) => {
    const headers = authHeaders();
    const baseUrl = apiUrl + '/v2/syncMyCampaign';
    const url = campaignId ? `${baseUrl}?campaign_id=${campaignId}` : baseUrl;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const republishCampaign = async (campaignId?: number, force: boolean = false) => {
    const headers = authHeaders();
    const baseUrl = apiUrl + '/v2/republishMyCampaign';
    const url = campaignId ? `${baseUrl}?campaign_id=${campaignId}` : baseUrl;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ force }),
    });
    const json = await resp.json();
    return json;
};

export const fetchMyCampaignId = async (id): Promise<MyCampaignResponse> => {
    const headers = authHeaders();
    const url = apiUrl + '/v2/myCampaignId/' + id;
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const fetchMyDisbursements = async (
    start_date: string | null = null,
    end_date: string | null = null,
    campaignId: string | null = null
) => {
    const headers = authHeaders();
    let url = apiUrl + '/v2/myDisbursements';
    if (start_date && end_date) {
        url += `?start_date=${start_date}&end_date=${end_date}&campaign_id=${campaignId}`;
    } else if (campaignId) {
        url += `?campaign_id=${campaignId}`;
    }
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const postCampaignUpdate = async (payload, campaignId) => {
    const headers = authHeaders();
    const baseUrl = apiUrl + '/v2/campaignUpdates';
    const url = campaignId ? baseUrl + '?campaign_id=' + campaignId : baseUrl;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    const json = await resp.json();
    return json;
};

export const postVendorAddress = async (payload, vendorId, campaignId) => {
    const headers = authHeaders();
    const baseUrl = apiUrl + `/v2/fundrequests/vendors/${vendorId}/address`;
    const url =
        campaignId
            ? baseUrl + '?campaign_id=' + campaignId
            : baseUrl;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    const json = await resp.json();
    return json;
}

export const createchangerequest = async (payload: any, vendorId: any) => {
    const headers = authHeaders();
    const url = apiUrl + `/v2/fundrequests/vendors/${vendorId}/changerequest`;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    if (!resp.ok) {
        throw new Error(`Error creating change request`);
    }
    const json = await resp.json();
    return json;
}

export const achDocumentPresignedUrlByCampaign = async (
    campaignId: string,
    fileName: string
) => {
    const headers = authHeaders();
    const url =
        apiUrl +
        '/v2/campaigns/' +
        campaignId +
        '/ach/presignedUrl?filename=' +
        fileName;
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const putCampaignUpdate = async (payload, id, campaignId) => {
    const headers = authHeaders();
    const baseUrl = apiUrl + '/v2/campaignUpdates/' + id;
    const url = campaignId ? baseUrl + '?campaign_id=' + campaignId : baseUrl;
    const resp = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
    });
    const json = await resp.json();
    return json;
};

export const deleteCampaignUpdate = async (id, campaignId) => {
    const headers = authHeaders();
    const baseUrl = apiUrl + '/v2/campaignUpdates/' + id;
    const url = campaignId ? baseUrl + '?campaign_id=' + campaignId : baseUrl;
    const resp = await fetch(url, {
        method: 'DELETE',
        headers,
    });
    return resp;
};

export const createAlbum = async (album_name, campaign_id) => {
    const headers = authHeaders();
    const url = apiUrl + '/v2/album/create';
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ album_name, campaign_id }),
    });
    const json = await resp.json();
    return json;
};

export const deleteAlbum = async (album_id, campaign_id) => {
    const headers = authHeaders();
    const url = apiUrl + '/v2/album/delete';
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ album_id, campaign_id }),
    });
    return resp;
};

export const deleteCampaignPhoto = async (photoId) => {
    const headers = authHeaders();
    const url = apiUrl + '/v2/albumPhoto/' + photoId;
    const resp = await fetch(url, {
        method: 'DELETE',
        headers,
    });
    return resp;
};

export const fetchCampaignPhotoPresignedUrl = async (fileName, campaignId) => {
    const headers = authHeaders();
    const url =
        apiUrl +
        '/v2/albumPhoto/presignUrl?campaign_id=' +
        campaignId +
        '&filename=' +
        fileName;
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const fetchThumbPhotoPresignedUrl = async (fileName, campaignId) => {
    const headers = authHeaders();
    const url =
        apiUrl +
        '/v2/campaignPhoto/thumb/presignUrl?campaign_id=' +
        campaignId +
        '&filename=' +
        fileName;
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const saveCampaignPhotoCrop = async (payload, campaignId) => {
    const headers = authHeaders();
    const url = apiUrl + '/v2/campaignPhotoCrop?campaign_id=' + campaignId;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    const json = await resp.json();
    return json;
};

export const saveThumbPhotoCrop = async (payload, campaignId) => {
    const headers = authHeaders();
    const url = apiUrl + '/v2/campaignThumb?campaign_id=' + campaignId;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    const json = await resp.json();
    return json;
};

export const fetchCampaignUpdatePresignedUrl = async (fileName, campaignId) => {
    const headers = authHeaders();
    const url =
        apiUrl +
        '/v2/campaignUpdates/presignUrl?filename=' +
        fileName +
        '&campaignId=' +
        campaignId;
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const uploadFile = async (fields, url, file) => {
    const formData = new FormData();
    Object.entries(fields).forEach(([k, v]) => {
        formData.append(k, v);
    });
    formData.append('file', file);
    const resp = await fetch(url, {
        method: 'POST',
        body: formData,
    });
    if (!resp.ok) {
        // S3 answers with an XML error body. Surface it instead of letting a
        // rejected upload look like a success and writing a dead URL to the form.
        throw new Error(
            `The file could not be saved to storage (error ${resp.status}).`
        );
    }
    return resp;
};

export const saveAlbumPhoto = async (payload) => {
    const headers = authHeaders();
    const url = apiUrl + '/v2/albumPhoto';
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    const json = await resp.json();
    return json;
};

export const saveCampaignPhoto = async (payload, campaign_id: string) => {
    const headers = authHeaders();
    const url = apiUrl + '/v2/campaignPhoto?campaign_id=' + campaign_id;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    const json = await resp.json();
    return json;
};

export const removeCampaignPhoto = async (campaign_id: string) => {
    const headers = authHeaders();
    const baseUrl = apiUrl + '/v2/campaignPhotoDelete';
    const url = baseUrl + '?campaign_id=' + campaign_id;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
    });
    return resp;
};

export const saveAlbumCaptions = async (
    albumId,
    photoCaptions,
    coverPhotoId
) => {
    const headers = authHeaders();
    const url = apiUrl + '/v2/album/saveCaptions';
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ albumId, photoCaptions, coverPhotoId }),
    });
    const json = await resp.json();
    return json;
};

export const saveAlbumName = async (payload) => {
    const headers = authHeaders();
    const url = apiUrl + '/v2/album/saveName';
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    const json = await resp.json();
    return json;
};

export const fetchSupporters = async (
    ntaf_id: string,
    filters,
    page = 1,
    pageSize = 20
): Promise<IDonationSummary> => {
    const headers = authHeaders();
    const filterParams = queryString.stringify(filters, {
        skipNull: true,
        skipEmptyString: true,
    });
    const url = `${apiUrl}/v2/campaignSupporters/${ntaf_id}?${filterParams}&page=${page}&pageSize=${pageSize}`;
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const fetchAllSupporters = async (
    ntaf_id: string,
    filters
): Promise<IDonationSummary> => {
    const headers = authHeaders();
    const filterParams = queryString.stringify(filters, {
        skipNull: true,
        skipEmptyString: true,
    });
    const url = `${apiUrl}/v2/campaignSupportersCSV/${ntaf_id}?${filterParams}`;
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const fetchGrants = async (ntaf: string): Promise<IGrantItem[]> => {
    const headers = authHeaders();
    const url = apiUrl + '/v2/campaignGrants/' + ntaf;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const fetchUpdates = async (
    id: string
): Promise<ICampaignUpdate[] | { detail: string }> => {
    const headers = authHeaders();
    let param = new URLSearchParams({
        campaign_id: id,
    }).toString();

    const url = apiUrl + `/v2/campaignUpdates?` + param;
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });

    const json = await resp.json();
    return json;
};

export const fetchExpenses = async (id: string): Promise<IExpenseItem> => {
    const headers = authHeaders();
    const url = apiUrl + '/v2/campaignExpenses/' + id;
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const createTeamMember = async (payload, campaign_id?: string) => {
    const headers = authHeaders();
    const baseUrl = apiUrl + '/v2/teammember';
    const url = campaign_id ? `${baseUrl}?campaign_id=${campaign_id}` : baseUrl;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    const json = await resp.json();
    return json;
};

export const putTeamMember = async (payload, campaign_id?: string) => {
    const headers = authHeaders();
    const baseUrl = apiUrl + '/v2/teammember';
    const url = campaign_id ? `${baseUrl}?campaign_id=${campaign_id}` : baseUrl;
    const resp = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
    });
    const json = await resp.json();
    return json;
};

export const fetchTeamMember = async (id) => {
    const headers = authHeaders();
    const url = apiUrl + '/v2/teammember/' + id;
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const deleteTeamMember = async (id, campaign_id?: string) => {
    const headers = authHeaders();
    const baseUrl = apiUrl + '/v2/teammember/' + id;
    const url = campaign_id ? `${baseUrl}?campaign_id=${campaign_id}` : baseUrl;
    const resp = await fetch(url, {
        method: 'DELETE',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const postCampaignContact = async (payload: any, campaignId?: string) => {
    const headers = authHeaders();
    const baseUrl = apiUrl + '/v2/campaignContact';
    const url = campaignId ? baseUrl + '?campaign_id=' + campaignId : baseUrl;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    const json = await resp.json();
    return json;
};

export const fetchPatient = async () => {
    const headers = authHeaders();
    const url = apiUrl + '/v2/patient';
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const putPatient = async (payload: any, campaignId?: string) => {
    const headers = authHeaders();
    const baseUrl = apiUrl + '/v2/patient';
    const url = campaignId ? baseUrl + '?campaign_id=' + campaignId : baseUrl;
    const resp = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
    });
    const json = await resp.json();
    return json;
};

export const fetchMyPerson = async () => {
    const headers = authHeaders();
    const url = apiUrl + '/v2/myProfile';
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const postEvent = async (payload, campaignId) => {
    const headers = authHeaders();
    const baseUrl = apiUrl + '/v2/event';
    const url = campaignId ? baseUrl + '?campaign_id=' + campaignId : baseUrl;

    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    const json = await resp.json();
    return json;
};

export const putEvent = async (payload, campaignId) => {
    const headers = authHeaders();
    const baseUrl = apiUrl + '/v2/event';
    const url = campaignId ? baseUrl + '?campaign_id=' + campaignId : baseUrl;

    const resp = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
    });
    const json = await resp.json();
    return json;
};

export const duplicateEvent = async (
    id: number,
    convertToV2 = false,
    fundraiserType?: 'event' | 'sale'
) => {
    const headers = authHeaders();
    const query = new URLSearchParams();
    if (convertToV2) query.set('convert_to_v2', 'true');
    if (fundraiserType) query.set('fundraiser_type', fundraiserType);
    const params = query.toString() ? `?${query.toString()}` : '';
    const url = `${apiUrl}/v2/event/duplicate/${id}${params}`;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const deleteGuestBookEntry = async (id: number) => {
    const headers = authHeaders();
    const url = apiUrl + '/v2/guestbook/' + id;
    const resp = await fetch(url, {
        method: 'DELETE',
        headers,
    });
    return resp;
};

export const syncFundraiser = async (eventId: number, campaignId?: number) => {
    const headers = authHeaders();
    const baseUrl = `${apiUrl}/v2/syncFundraiser/${eventId}`;
    const url = campaignId ? `${baseUrl}?campaign_id=${campaignId}` : baseUrl;
    const resp = await fetch(url, { method: 'POST', headers });
    const json = await resp.json();
    return json;
};

export const deleteFundraiser = async (id: number) => {
    const headers = authHeaders();
    const url = apiUrl + '/v2/fundraisers/' + id;

    const resp = await fetch(url, {
        method: 'DELETE',
        headers,
    });
    return resp;
};

export const fetchFeatureImagePresignedUrl = async (
    fileName: string,
    campaignId: string
) => {
    const headers = authHeaders();
    const url =
        apiUrl +
        '/v2/event-photo/presignUrl?campaign_id=' +
        campaignId +
        '&filename=' +
        fileName;
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    if (!resp.ok) {
        throw new Error(
            `Could not get permission to upload the file (error ${resp.status}).`
        );
    }
    const json = await resp.json();
    if (!json?.url || !json?.fields) {
        throw new Error('Could not get permission to upload the file.');
    }
    return json;
};

export const flyerImagePresignedUrl = async (
    fileName: string,
    campaignId: string
) => {
    const headers = authHeaders();
    const url =
        apiUrl +
        '/v2/flyer/presignUrl?campaign_id=' +
        campaignId +
        '&filename=' +
        fileName;
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    if (!resp.ok) {
        throw new Error(
            `Could not get permission to upload the file (error ${resp.status}).`
        );
    }
    const json = await resp.json();
    if (!json?.url || !json?.fields) {
        throw new Error('Could not get permission to upload the file.');
    }
    return json;
};

export const fetchCampaignMaterials = async (campaignId?: string) => {
    const headers = authHeaders();
    const baseUrl = apiUrl + '/v2/campaignMaterials';
    const url = campaignId ? baseUrl + '?campaign_id=' + campaignId : baseUrl;
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    const json = await resp.json();
    return json;
};

// The announcement bar for the dashboard, or null when none is showing today.
// It is not filtered by campaign status: the caller does that, because a
// superuser's campaign comes from the URL, not the account.
export const fetchCampaignMessage = async (): Promise<ICampaignMessage | null> => {
    const headers = authHeaders();
    const resp = await fetch(apiUrl + '/v2/campaignMessage', {
        method: 'GET',
        headers,
    });
    if (!resp.ok) return null;
    const json = await resp.json();
    return json.message;
};
